import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../api";
import { useT } from "../i18n";
import Header from "../components/Header";
import AdminProForm from "../components/AdminProForm";
import AdminProRow from "../components/AdminProRow";
import AdminProsToolbar from "../components/AdminProsToolbar";
import AdminReportsPanel from "../components/AdminReportsPanel";

const EMPTY_FORM = { name: "", specialty: "orthophoniste", commune: "Baie-Mahault", phone: "", contact_email: "", note: "", languages: ["fr"], places_total: null, places_available: null, wait_estimate: "", status: "disponible", approved: true, consent: false };

export default function AdminDashboard() {
  const { t, lang } = useT();
  const [tab, setTab] = useState("pros");
  const [pros, setPros] = useState([]);
  const [reports, setReports] = useState([]);
  const [meta, setMeta] = useState({ specialties: [], communes: [] });
  const [editing, setEditing] = useState(null); // null | "new" | pro object
  const [form, setForm] = useState(EMPTY_FORM);
  const [inviteEmail, setInviteEmail] = useState("");

  const loadPros = useCallback(() =>
    api.get("/admin/pros").then((res) => setPros(res.data.pros)).catch((err) => toast.error(formatApiError(err))), []);
  const loadReports = useCallback(() =>
    api.get("/admin/reports").then((res) => setReports(res.data.reports)).catch(() => {}), []);

  const sendInvite = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/invitations", { email: inviteEmail });
      toast.success(t("admin.invited"));
      setInviteEmail("");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/admin/pros/import", fd);
      toast.success(t("admin.import.result", { n: res.data.created, e: res.data.errors.length }));
      loadPros();
    } catch (err) { toast.error(formatApiError(err)); }
    e.target.value = "";
  };

  useEffect(() => {
    api.get("/meta").then((res) => setMeta(res.data)).catch(() => {});
    loadPros(); loadReports();
  }, [loadPros, loadReports]);

  const locale = lang === "en" ? "en-GB" : "fr-FR";

  const startEdit = (pro) => {
    setEditing(pro);
    setForm(pro === "new" ? EMPTY_FORM : {
      name: pro.name, specialty: pro.specialty, commune: pro.commune, phone: pro.phone || "",
      contact_email: pro.contact_email || "", note: pro.note || "",
      languages: pro.languages || ["fr"], status: pro.raw_status, approved: pro.approved,
      places_total: pro.places_total ?? null, places_available: pro.places_available ?? null,
      wait_estimate: pro.wait_estimate || "",
      consent: !!pro.consent_at,
    });
  };

  const savePro = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") await api.post("/admin/pros", form);
      else await api.put(`/admin/pros/${editing.id}`, form);
      toast.success(t("admin.saved"));
      setEditing(null);
      loadPros();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deletePro = async (pro) => {
    if (!window.confirm(pro.name)) return;
    try { await api.delete(`/admin/pros/${pro.id}`); loadPros(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const nudgePro = async (pro) => {
    try { await api.post(`/admin/pros/${pro.id}/nudge`); toast.success(t("admin.nudged")); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const resolveReport = async (report) => {
    try { await api.put(`/admin/reports/${report.id}/resolve`); loadReports(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-6xl mx-auto px-4 md:px-8 py-10">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <h1 className="text-3xl font-extrabold tracking-tight text-[#1F2937]" style={{ fontFamily: "Manrope" }}>{t("admin.title")}</h1>
          <div className="flex gap-2">
            {[["pros", t("admin.tab.pros")], ["reports", `${t("admin.tab.reports")} (${reports.filter((r) => !r.resolved).length})`]].map(([key, label]) => (
              <button key={key} data-testid={`admin-tab-${key}`} onClick={() => setTab(key)}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold border transition-colors duration-200 ${
                  tab === key ? "bg-[#0D4045] text-[#FDFBF7] border-[#0D4045]" : "bg-white text-[#0D4045] border-[#0D4045]/15"
                }`}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {tab === "pros" && (
          <section className="mt-8" data-testid="admin-pros-section">
            <AdminProsToolbar inviteEmail={inviteEmail} setInviteEmail={setInviteEmail}
              onInvite={sendInvite} onImport={importCsv} onAdd={() => startEdit("new")} />
            {editing && (
              <AdminProForm form={form} setForm={setForm} meta={meta} onSubmit={savePro} onCancel={() => setEditing(null)} />
            )}

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {pros.map((pro) => (
                <AdminProRow key={pro.id} pro={pro} locale={locale}
                  onEdit={startEdit}
                  onApprove={(p) => { startEdit(p); setForm((f) => ({ ...f, approved: true })); }}
                  onDelete={deletePro} onNudge={nudgePro} />
              ))}
            </div>
          </section>
        )}

        {tab === "reports" && (
          <AdminReportsPanel reports={reports} locale={locale} onResolve={resolveReport} />
        )}
      </main>
    </div>
  );
}
